import { useState, type MouseEvent } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesomeRounded,
  ExpandMoreRounded,
  LogoutOutlined,
  MenuOpenOutlined,
  MenuOutlined,
  PersonOutlineOutlined,
  SettingsOutlined,
  ViewSidebarOutlined,
  WebOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useLogout } from '@/hooks/useAuthActions';
import BranchSwitcher from './BranchSwitcher';
import { GlobalSearch, HelpMenu, NewMenu, NotificationsBell } from './TopbarActions';
import { useBranding } from '@/hooks/usePublicBranding';
import { colorFromString, initials } from '@/utils/format';
import { useNavLayout } from '@/hooks/useNavLayout';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import ModuleNav from './ModuleNav';
import { usePageTitleValue } from './PageTitleContext';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';

interface Props {
  onMenuClick: () => void;
}

export default function Topbar({ onMenuClick }: Props) {
  const { profile } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const { layout, setLayout } = useNavLayout();
  const isTop = layout === 'topnav';
  const { collapsed, toggle: toggleCollapsed } = useSidebarCollapsed();
  const railWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const pageTitle = usePageTitleValue();
  const { companyName } = useBranding();
  // "CompanyAdmin" → "Company Admin": the role as people say it.
  const role = (profile?.roles?.[0] ?? profile?.userType ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);

  const handleLogout = async () => {
    close();
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: '#fff',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        // Inset past the rail rather than running the full width. The rail is full height now and
        // carries the company mark at its top, so it owns the top-left corner; the header begins
        // where the rail ends. On a phone there is no rail, so it reaches the edge.
        width: { xs: '100%', md: isTop ? '100%' : `calc(100% - ${railWidth}px)` },
        ml: { xs: 0, md: isTop ? 0 : `${railWidth}px` },
        transition: 'width .25s ease, margin .25s ease',
        // Above the permanent drawer, so the drawer tucks under this bar rather than over it.
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{
        gap: { xs: 0.25, sm: 0.75, md: 1 },
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1, sm: 2 },
        minWidth: 0,
      }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { md: 'none' } }}
          aria-label="open navigation"
        >
          <MenuOutlined />
        </IconButton>

        {/* Desktop sidebar collapse toggle (sidebar layout only). */}
        {!isTop && (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <IconButton
              edge="start"
              onClick={toggleCollapsed}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              aria-label={collapsed ? 'expand sidebar' : 'collapse sidebar'}
            >
              <MenuOpenOutlined sx={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform .25s ease' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* A heading the current screen asked for. Absent on every screen that does not set one,
            which is all of them by default. */}
        {/* Shown at every width, clipped rather than dropped: on a phone this is the only thing on
            screen that says which page you are on. */}
        {pageTitle && (
          <Stack
            direction="row" alignItems="center" spacing={1}
            sx={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}
          >
            <AutoAwesomeRounded sx={{ fontSize: 19, color: 'primary.main', flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: { xs: 14.5, sm: 17 }, fontWeight: 700 }}>
              {pageTitle}
            </Typography>
          </Stack>
        )}

        {/* Search (O11). */}
        <Box sx={{ ml: 1 }}>
          <GlobalSearch />
        </Box>

        {/* Top Navigation: horizontal module menu (desktop). Sidebar layout keeps a plain spacer. */}
        {isTop && <ModuleNav sx={{ mx: 1, display: { xs: 'none', md: 'flex' }, flexGrow: 1, minWidth: 0 }} />}
        <Box sx={{ flexGrow: 1 }} />

        <NewMenu />
        <NotificationsBell />
        <HelpMenu />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5, display: { xs: 'none', sm: 'block' } }} />

        {/* Who is signed in, then the avatar -- one target, so the name is as clickable as the
            picture. The name goes before the avatar disappears: a face with no name still
            identifies the account to the person looking at it. */}
        <Tooltip title="Account">
          <Box
            component="button"
            onClick={open}
            aria-label="account menu"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              background: 'none', border: 0, cursor: 'pointer',
              borderRadius: 2, p: 0.5, ml: 0.5,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                borderRadius: 2,
                bgcolor: colorFromString(companyName ?? profile?.fullName ?? 'U'),
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {initials(companyName ?? profile?.fullName)}
            </Avatar>

            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, maxWidth: 200 }}>
                {companyName ?? profile?.fullName}
              </Typography>
              <Typography noWrap sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3, maxWidth: 200 }}>
                {[profile?.fullName, role].filter(Boolean).join(' · ')}
              </Typography>
            </Box>

            <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }} />
          </Box>
        </Tooltip>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={close}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: { mt: 1, minWidth: 240, maxWidth: 'calc(100vw - 16px)', borderRadius: 3 } } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" noWrap>
              {profile?.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {profile?.email}
            </Typography>
          </Box>
          <Divider />
          {/* Workspace and branch live in the header from md / lg up. Below that the header has no
              room for them, so the same switchers appear here — otherwise a phone could not change
              branch at all. The components are the header's own, not copies. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              px: 2,
              py: 1.25,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <BranchSwitcher />
          </Box>
          <MenuItem onClick={() => { close(); navigate('/profile'); }}>
            <ListItemIcon>
              <PersonOutlineOutlined fontSize="small" />
            </ListItemIcon>
            My Profile
          </MenuItem>
          <MenuItem onClick={() => { close(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsOutlined fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { close(); setLayout(isTop ? 'sidebar' : 'topnav'); }}>
            <ListItemIcon>
              {isTop ? <ViewSidebarOutlined fontSize="small" /> : <WebOutlined fontSize="small" />}
            </ListItemIcon>
            {isTop ? 'Left sidebar layout' : 'Top navigation layout'}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <LogoutOutlined fontSize="small" color="error" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
